import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Search as SearchIcon } from "@mui/icons-material";
import AddToTeam from "./AddToTeam";
import { useDispatch, useSelector } from "react-redux";
import { removeTeamMember } from "../../../redux/Slice/Settings/addfunctionsSlice";
import {
  fetchActiveMembers,
  fetchTeams,
  setSelectedTeam,
  setTeamLeader,
} from "../../../redux/Slice/Settings/settingsSlice";
import { toast } from "react-hot-toast";
import { isSubscriptionBlockedError, checkSubscriptionActive } from "../../../utils/subscriptionError";
import { getAuthToken } from "../../../utils/getAuthToken";
import AuthView from "../../../utils/AuthView";

// Match TeamMembers table typography on md/lg only
const tableHeaderFontSx = { fontSize: { md: "0.875rem", lg: "0.9rem" } };
const tableBodyFontSx = { fontSize: { md: "0.875rem", lg: "0.875rem" } };

const MEMBER_COLUMNS = [
  { id: "name", label: "Name", width: "16%" },
  { id: "email", label: "Email", width: "30%" },
  { id: "role", label: "Role", width: "18%" },
  { id: "leader", label: "Leader", width: "24%" },
  { id: "actions", label: "Actions", width: "12%" },
];

const columnGap = { xs: "12px", sm: "16px", md: "20px", lg: "28px" };

const memberColumnCellSx = (col) => ({
  width: col.width,
  maxWidth: col.width,
  ...(col.id === "email" || col.id === "name"
    ? { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
    : null),
  ...(col.id === "role" ? { whiteSpace: "nowrap" } : null),
  ...(col.id === "name"
    ? {
        pl: { xs: "8px", sm: "12px", md: "16px" },
        pr: columnGap,
      }
    : null),
  ...(col.id === "email"
    ? { pl: columnGap, pr: columnGap }
    : null),
  ...(col.id === "role"
    ? { pl: columnGap, pr: columnGap }
    : null),
  ...(col.id === "leader"
    ? {
        pl: columnGap,
        pr: { xs: "4px", sm: "6px", md: "8px" },
      }
    : null),
  ...(col.id === "actions"
    ? {
        pl: { xs: "4px", sm: "6px", md: "8px" },
        pr: { xs: "8px", sm: "12px", md: "16px" },
      }
    : null),
});

const getMemberRoleName = (member, roleByMemberId) => {
  if (!member) return "";
  if (member.Roles?.roleName) return member.Roles.roleName;
  if (typeof member.Roles === "object" && member.Roles?.name) return member.Roles.name;
  const id = member._id ? String(member._id) : "";
  return roleByMemberId[id] || "";
};

const ShowMembers = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openModal, setOpenModal] = useState(false);
  const [leaderSavingId, setLeaderSavingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();

  const { selectedTeam, teamMembers, activeMembers } = useSelector(
    (state) => state.settings
  );
  const TeamPermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Team || {}
  );

  const roleByMemberId = useMemo(() => {
    const map = {};
    [...(teamMembers || []), ...(activeMembers || [])].forEach((m) => {
      const roleName = m?.Roles?.roleName;
      if (m?._id && roleName) map[String(m._id)] = roleName;
    });
    return map;
  }, [teamMembers, activeMembers]);

  const members = useMemo(() => selectedTeam?.members || [], [selectedTeam]);
  const teamId = selectedTeam?._id;

  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return members;
    const lower = searchQuery.toLowerCase();
    return members.filter(
      (m) =>
        (m.name && m.name.toLowerCase().includes(lower)) ||
        (m.email && m.email.toLowerCase().includes(lower)) ||
        (getMemberRoleName(m, roleByMemberId) &&
          getMemberRoleName(m, roleByMemberId).toLowerCase().includes(lower))
    );
  }, [members, searchQuery, roleByMemberId]);

  const memberCount = filteredMembers.length;

  useEffect(() => {
    dispatch(fetchActiveMembers());
  }, [dispatch]);

  // Reset page when team changes, members change, or search changes
  useEffect(() => {
    setPage(0);
  }, [teamId, members.length, searchQuery]);

  const paginatedMembers = useMemo(
    () => filteredMembers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredMembers, page, rowsPerPage]
  );

  if (!selectedTeam) return null;

  const containerSx = {
    padding: { xs: "12px", sm: "20px", md: "20px", lg: "20px", xl: "20px" },
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    borderRadius: "8px",
    backgroundColor: "background.paper",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    flex: 1,
    color: "text.primary",
    overflow: "hidden",
  };

  // Show AuthView if user doesn't have Team.Edit permission
  if (!TeamPermissions.Edit) {
    return (
      <Box sx={containerSx}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => dispatch(setSelectedTeam(null))}
          sx={{
            textTransform: "capitalize",
            fontWeight: "bold",
            color: "primary.main",
            alignSelf: "flex-start",
            "&:hover": {
              backgroundColor: "transparent",
              "& .MuiButton-startIcon": { color: "primary.dark" },
            },
          }}
        >
          Back
        </Button>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            paddingLeft: "2rem",
          }}
        >
          <AuthView />
        </Box>
      </Box>
    );
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenModal = async () => {
    const { blocked, message } = await checkSubscriptionActive(getAuthToken);
    if (blocked) { toast.error(message, { id: "subscription-blocked", duration: 5000 }); return; }
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const currentLeaderId = selectedTeam.teamLeaderId?._id
    ? String(selectedTeam.teamLeaderId._id)
    : selectedTeam.teamLeaderId
      ? String(selectedTeam.teamLeaderId)
      : null;

  const handleSetLeader = async (memberId) => {
    const { blocked, message } = await checkSubscriptionActive(getAuthToken);
    if (blocked) { toast.error(message, { id: "subscription-blocked", duration: 5000 }); return; }
    const nextLeaderId = currentLeaderId === String(memberId) ? null : memberId;
    setLeaderSavingId(memberId);
    try {
      const team = await dispatch(
        setTeamLeader({ teamId: selectedTeam._id, teamLeaderId: nextLeaderId })
      ).unwrap();
      dispatch(setSelectedTeam(team));
      dispatch(fetchTeams());
      toast.success(nextLeaderId ? "Team leader updated." : "Team leader removed.");
    } catch (error) {
      if (!isSubscriptionBlockedError(error)) {
        toast.error(error || "Failed to update team leader.");
      }
    } finally {
      setLeaderSavingId(null);
    }
  };

  const handleRemoveMember = async (memberId) => {
    const { blocked, message } = await checkSubscriptionActive(getAuthToken);
    if (blocked) { toast.error(message, { id: "subscription-blocked", duration: 5000 }); return; }

    dispatch(removeTeamMember({ teamId: selectedTeam._id, memberId }))
      .unwrap()
      .then((response) => {
        const updatedTeam =
          response?.team?._id
            ? response.team
            : {
                ...selectedTeam,
                members: selectedTeam.members.filter((m) => m._id !== memberId),
              };
        dispatch(setSelectedTeam(updatedTeam));
        dispatch(fetchTeams());
        const nextCount = updatedTeam.members?.length ?? 0;
        const maxPage = Math.max(0, Math.ceil(nextCount / rowsPerPage) - 1);
        if (page > maxPage) setPage(maxPage);
        toast.success("Member removed successfully.");
      })
      .catch((error) => {
        console.error("Failed to remove member:", error);
        if (!isSubscriptionBlockedError(error)) {
          toast.error(typeof error === "string" ? error : "Failed to remove member.");
        }
      });
  };

  const indexOfFirst = page * rowsPerPage;
  const indexOfLast = Math.min(indexOfFirst + rowsPerPage, memberCount);

  return (
    <Box sx={containerSx}>
      {/* Back button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => dispatch(setSelectedTeam(null))}
        sx={{
          textTransform: "capitalize",
          fontWeight: "bold",
          color: "primary.main",
          alignSelf: "flex-start",
          flexShrink: 0,
          marginBottom: "0.75rem",
          "&:hover": {
            backgroundColor: "transparent",
            "& .MuiButton-startIcon": { color: "primary.dark" },
          },
        }}
      >
        Back
      </Button>

      {/* Search bar + Add To Team button — same layout as Teams main list */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: "1rem",
          gap: { xs: 0, sm: 2 },
          flexShrink: 0,
        }}
      >
        <TextField
          fullWidth
          placeholder="Search members..."
          variant="outlined"
          size={isXs ? "small" : "medium"}
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
          sx={{
            flex: 1,
            "& .MuiInputLabel-root": { color: "text.secondary" },
            "& .MuiInputBase-input": { color: "text.primary" },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: (t) =>
                  t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
              },
              "&:hover fieldset": {
                borderColor: (t) =>
                  t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
              },
              "&.Mui-focused fieldset": { borderColor: "primary.main" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          size={isXs ? "small" : "medium"}
          startIcon={
            <AddCircleOutlineIcon
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
            />
          }
          onClick={handleOpenModal}
          sx={{
            borderColor: "primary.main",
            color: "primary.main",
            borderRadius: "0.8rem",
            padding: { xs: "4px 8px", sm: "0.5rem 1rem" },
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            textTransform: "none",
            marginLeft: { xs: 1, sm: 2 },
            whiteSpace: "nowrap",
            flexShrink: 0,
            "&:hover": {
              backgroundColor: "rgba(14, 144, 70, 0.08)",
              borderColor: "primary.main",
            },
          }}
        >
          Add To Team
        </Button>
      </Box>

      {/* Team name — inside box, above table columns */}
      <Typography
        variant="h6"
        component="h2"
        sx={{
          fontWeight: "bold",
          color: "text.primary",
          fontSize: { xs: "1rem", md: "1.15rem", lg: "1.25rem" },
          textTransform: "capitalize",
          marginBottom: "0.75rem",
          flexShrink: 0,
        }}
      >
        {selectedTeam.teamName}
      </Typography>

      {/* Members Table — scrollable body, direct flex child (no wrapper) */}
        <TableContainer
          component={Paper}
          sx={{
            flex: 1,
            minHeight: 0,
            maxHeight: {
              xs: "none",
              sm: "none",
              md: "none",
              lg: "calc(100vh - 320px)",
              xl: "calc(100vh - 320px)",
            },
            boxShadow: "none",
            backgroundColor: "background.paper",
            overflow: "auto",
            scrollbarWidth: { xs: "thin", sm: "thin", md: "thin", lg: "thin", xl: "thin" },
            "&::-webkit-scrollbar": {
              width: { xs: "4px", sm: "4px", md: "3px", lg: "3px", xl: "3px" },
              height: { xs: "4px", sm: "4px", md: "3px", lg: "3px", xl: "3px" },
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: (t) => t.palette.mode === "dark" ? "#1a1a1a" : "#f1f1f1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: (t) => t.palette.mode === "dark" ? "#555" : "#888",
              borderRadius: "10px",
              "&:hover": {
                backgroundColor: (t) => t.palette.mode === "dark" ? "#777" : "#555",
              },
            },
            border: (t) =>
              t.palette.mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.12)"
                : "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: "1rem",
            "& .MuiTableCell-root": {
              borderBottom: (t) =>
                t.palette.mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.12)"
                  : "1px solid rgba(0, 0, 0, 0.12)",
              color: "text.primary",
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              backgroundColor: "background.paper",
              color: "text.primary",
              fontWeight: "bold",
              ...tableHeaderFontSx,
              padding: { xs: "8px 4px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" },
            },
            "& .MuiTableBody-root .MuiTableCell-root": {
              ...tableBodyFontSx,
              padding: { xs: "8px 4px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" },
            },
            "& .MuiTableRow-root:hover": {
              backgroundColor: (t) =>
                t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                {MEMBER_COLUMNS.map((col) => (
                  <TableCell
                    key={col.id}
                    sx={{
                      ...memberColumnCellSx(col),
                      fontWeight: "bold",
                      backgroundColor: "background.paper",
                      ...tableHeaderFontSx,
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedMembers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, ...tableBodyFontSx }}>
                    <Typography color="text.secondary" sx={tableBodyFontSx}>
                      {searchQuery ? "No members match your search." : "No team members found."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedMembers.map((member) => (
                  <TableRow key={member._id}>
                    <TableCell sx={{ ...tableBodyFontSx, ...memberColumnCellSx(MEMBER_COLUMNS[0]) }}>
                      {member.name}
                    </TableCell>
                    <TableCell sx={{ ...tableBodyFontSx, ...memberColumnCellSx(MEMBER_COLUMNS[1]) }}>
                      {member.email}
                    </TableCell>
                    <TableCell sx={{ ...tableBodyFontSx, ...memberColumnCellSx(MEMBER_COLUMNS[2]) }}>
                      {getMemberRoleName(member, roleByMemberId) || "—"}
                    </TableCell>
                    <TableCell sx={memberColumnCellSx(MEMBER_COLUMNS[3])}>
                      <Button
                        variant={currentLeaderId === String(member._id) ? "contained" : "outlined"}
                        size="small"
                        disabled={leaderSavingId === member._id}
                        onClick={() => handleSetLeader(member._id)}
                        sx={{ borderRadius: "0.8rem", textTransform: "none" }}
                      >
                        {currentLeaderId === String(member._id) ? "Leader" : "Set leader"}
                      </Button>
                    </TableCell>
                    <TableCell sx={memberColumnCellSx(MEMBER_COLUMNS[4])}>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleRemoveMember(member._id)}
                        sx={{ borderRadius: "0.8rem", textTransform: "none" }}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

      {/* Custom pagination — same style as Teams main list, centered */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: { xs: "0.5rem", sm: "0.5rem", md: "0.75rem" },
          flexShrink: 0,
          gap: { xs: 1, sm: 2 },
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem" } }}
        >
          {memberCount === 0 ? "0" : indexOfFirst + 1}–{indexOfLast} of {memberCount}
        </Typography>

        <FormControl
          size="small"
          sx={{
            minWidth: { xs: "110px", sm: "130px", md: "130px" },
            "& .MuiInputBase-root": {
              fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem" },
              minHeight: { xs: "30px", sm: "34px", md: "34px" },
            },
          }}
        >
          <InputLabel sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem" } }}>
            Rows per page
          </InputLabel>
          <Select
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            label="Rows per page"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem" } }}
          >
            {[5, 10, 15, 20, 25, 50].map((n) => (
              <MenuItem
                key={n}
                value={n}
                sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.875rem" } }}
              >
                {n}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex" }}>
          <IconButton
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 0}
            size="small"
            sx={{
              padding: { xs: "2px", sm: "4px", md: "3px" },
              "& .MuiSvgIcon-root": {
                fontSize: { xs: "1rem", sm: "1.2rem", md: "1.1rem" },
              },
              color: (t) => t.palette.mode === "dark" ? "text.secondary" : "rgba(0, 0, 0, 0.6)",
              "&.Mui-disabled": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton
            onClick={() => setPage((p) => p + 1)}
            disabled={indexOfLast >= memberCount}
            size="small"
            sx={{
              padding: { xs: "2px", sm: "4px", md: "3px" },
              "& .MuiSvgIcon-root": {
                fontSize: { xs: "1rem", sm: "1.2rem", md: "1.1rem" },
              },
              color: (t) => t.palette.mode === "dark" ? "text.secondary" : "rgba(0, 0, 0, 0.6)",
              "&.Mui-disabled": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Modal for adding members */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "background.paper",
            color: "text.primary",
          },
        }}
      >
        <AddToTeam open={openModal} onClose={handleCloseModal} />
      </Dialog>
    </Box>
  );
};

export default ShowMembers;
